import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useAppContext } from '../context/AppContext';
import Select from 'react-select';
import { FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: ${props => props.theme.spacing.small};
  justify-content: center;
`;

const StyledSelect = styled(Select)`
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  border-radius: 20px;
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  margin: 0 ${props => props.theme.spacing.small};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  margin-left: ${props => props.theme.spacing.small};
`;

const InfoLabel = styled.span`
  font-size: '1.8rem';
  color: ${props => props.theme.colors.text};
  font-weight: bold;
  margin-right: ${props => props.theme.spacing.medium};
  margin-left: ${props => props.theme.spacing.medium};
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
    borderColor: 'rgb(71, 71, 71)',
    borderRadius: '20px',
    padding: '0.25rem',
    color: 'white',
    width: '20rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FDBA31' : 'rgb(71, 71, 71)',
    color: state.isSelected ? 'black' : 'white',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
  }),
};

function SessionsDropDown() {
  const {
    athlete,
    currentAthleteEvaluations,
    currentAthleteSessionList,
    currentAthleteSessionIndex,
    setCurrentAthleteSessionIndex,
    editingEvalModeIsActive,
    setEditingEvalModeIsActive,
    setInputValuesObject,
    inputValuesObject,
    editEvaluation,
    loadAthleteEvaluations,
    currentDataType,
    evalInputModeActive, 
    setEvalInputModeActive,
    groupAnalysisMode,
    setGroupAnalysisObject,
  } = useAppContext();

  const handleSessionSelectionChange = useCallback((selectedOption) => {
    const selectedIndex = currentAthleteSessionList.findIndex(session => session === selectedOption.value);
    const newIndex = selectedIndex === -1 ? 0 : selectedIndex;
    setCurrentAthleteSessionIndex(newIndex);
    setInputValuesObject(currentAthleteEvaluations[newIndex]);
  }, [currentAthleteEvaluations, currentAthleteSessionList, setCurrentAthleteSessionIndex, setInputValuesObject]);

  const handleEvaluationDelete = useCallback(async () => {
    if (currentAthleteEvaluations.length === 0) return;

    const result = await Swal.fire({
      title: 'Confirm evaluation deletion?',
      text: 'This action is not reversible',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: "#FDBA31",
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}api/flask`, {
          functionName: 'DeleteEvaluation',
          inputArray: currentAthleteEvaluations[currentAthleteSessionIndex]
        });
        await loadAthleteEvaluations(athlete, currentDataType);
        setGroupAnalysisObject({});
        Swal.fire('Deleted!', 'The evaluation has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting evaluation:', error);
        Swal.fire('Error', 'Failed to delete evaluation', 'error');
      }
    }
  }, [currentAthleteEvaluations, currentAthleteSessionIndex, loadAthleteEvaluations, athlete, currentDataType, setGroupAnalysisObject]);

  const handleEvaluationEdit = useCallback(async () => {
    if (currentAthleteEvaluations.length === 0) return;

    if (editingEvalModeIsActive) {
      const result = await Swal.fire({
        title: 'Confirm save evaluation edit',
        text: 'What do you want to do with the edits to this evaluation?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        confirmButtonColor: "#FDBA31",
        denyButtonText: 'Discard',
        denyButtonColor: "black",
      });

      if (result.isConfirmed) {
        inputValuesObject['dataType'] = evalInputModeActive ? 'Eval' : 'Training';
        await editEvaluation(inputValuesObject, athlete);
      }

      await loadAthleteEvaluations(athlete, currentDataType);
      setEditingEvalModeIsActive(false);
    } else {
      const result = await Swal.fire({
        title: 'Confirm start evaluation edit',
        text: 'Are you sure you want to begin editing this evaluation?',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        confirmButtonColor: "#FDBA31",
      });

      if (result.isConfirmed) {
        setEvalInputModeActive(inputValuesObject['dataType'] === 'Eval');
        setEditingEvalModeIsActive(true);
      }
    }
  }, [currentAthleteEvaluations, editingEvalModeIsActive, editEvaluation, inputValuesObject, athlete, loadAthleteEvaluations, setEditingEvalModeIsActive, currentDataType, evalInputModeActive, setEvalInputModeActive]);

  const noAthleteSelected = !athlete;
  const noSessions = currentAthleteSessionList.length === 0;

  return (
    <DropdownContainer>
      <DropdownRow>
        <InfoLabel>{currentAthleteSessionList.length}</InfoLabel>
        <StyledSelect
          options={currentAthleteSessionList.map(session => ({ value: session, label: session }))}
          value={currentAthleteSessionList[currentAthleteSessionIndex] ? { value: currentAthleteSessionList[currentAthleteSessionIndex], label: currentAthleteSessionList[currentAthleteSessionIndex] } : null}
          onChange={handleSessionSelectionChange}
          styles={customSelectStyles}
          isDisabled={noAthleteSelected || noSessions}
          placeholder={noAthleteSelected ? 'No athlete selected' : noSessions ? (currentDataType === 'Any' ? 'No evaluations found' : `No ${currentDataType.toLowerCase()} sessions found`) : 'Select session'}
        />
        <IconButton onClick={handleEvaluationDelete} disabled={noAthleteSelected || noSessions}>
          <FaTrash />
        </IconButton>
        <ButtonGroup>
          <ActionButton onClick={handleEvaluationEdit} disabled={noAthleteSelected || noSessions || groupAnalysisMode}>
            {editingEvalModeIsActive ? <FaSave /> : <FaEdit />}
            {editingEvalModeIsActive ? 'Save' : 'Edit'}
          </ActionButton>
        </ButtonGroup>
      </DropdownRow>
    </DropdownContainer>
  );
}

export default SessionsDropDown;